import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheck, FaTimes } from 'react-icons/fa';
import './About.css';
import { Helmet } from 'react-helmet';

const WhyChooseUs = () => {
  const benefits = [
    {
      // icon: '🏆',
      title: 'Professional & Experienced Coaching',
      description: 'Our academy is led by trained and experienced badminton coaches who possess deep knowledge of the game. Each coach focuses on correct techniques, strategic gameplay, and continuous skill improvement. With personalized guidance and regular feedback, players receive the attention they need to grow consistently.'
    },
    {
      title: 'Structured & Scientific Training Methodology',
      description: 'We follow a well-planned and systematic training structure that focuses on long-term player development. Training sessions are designed to improve technical skills, footwork, speed, endurance, and match performance. Our step-by-step approach ensures steady progress for players at all levels.'
    },
    {
      title: 'International-Standard Infrastructure',
      description: 'Imperial Badminton Academy provides a professional training environment with high-quality indoor courts, proper lighting, and safe playing surfaces. Our infrastructure is designed to enhance performance while reducing the risk of injuries, allowing players to train with confidence and comfort.'
    },
    {
      title: 'Individual Attention & Player Development',
      description: 'We strongly believe that every player is unique. Our training philosophy emphasizes individual assessment, personalized coaching, and goal-based development. Optimal coach-to-player ratios ensure focused training and continuous performance monitoring.'
    },
    {
      title: 'Focus on Fitness & Injury Prevention',
      description: 'Badminton demands high physical fitness. Our training includes strength conditioning, agility drills, endurance workouts, and flexibility exercises. Special attention is given to injury prevention and recovery, helping players maintain peak performance throughout the year.'
    },
    {
      title: 'Mental Strength & Match Readiness',
      description: 'Success in badminton requires strong mental focus and confidence. We train players to handle match pressure, stay calm during critical moments, and maintain a winning mindset. This mental conditioning prepares players for competitive environments and real match situations.'
    },
    {
      title: 'Discipline, Values & Sportsmanship',
      description: 'At Imperial Badminton Academy, discipline and character are as important as skills. We instill values such as respect, dedication, teamwork, and sportsmanship. Our players learn to win with humility and accept challenges with confidence.'
    },
    {
      title: 'Proven Track Record & Continuous Growth',
      description: 'With years of experience and a strong training foundation, our academy has consistently produced disciplined and confident players. Our continuous growth reflects our commitment to quality coaching and player success.'
    },
    {
      title: 'Safe, Supportive & Motivating Environment',
      description: 'We provide a positive and encouraging atmosphere where players feel motivated to push their limits. Our academy promotes teamwork, mutual respect, and healthy competition, ensuring overall personal and athletic development.'
    },
    {
      title: 'Commitment to Excellence',
      description: 'We are dedicated to maintaining high training standards and continuously upgrading our methods to align with modern badminton practices. Our commitment ensures that every player receives the best possible guidance and support.'
    },
    {
      title: 'Our Promise',
      description: 'When you choose Imperial Badminton Academy, you choose professionalism, discipline, and a clear pathway to success. We are committed to helping every player unlock their true potential and achieve excellence in badminton.'
    },
  ];

  const comparison = [
    { feature: 'International Standard Courts', imperial: true, others: false },
    { feature: 'Certified Professional Coaches', imperial: true, others: 'partial' },
    { feature: 'Video Analysis & Feedback', imperial: true, others: false },
    { feature: 'Dedicated Fitness Center', imperial: true, others: false },
    { feature: 'Personalized Training Plans', imperial: true, others: false },
    { feature: 'Tournament Preparation Support', imperial: true, others: 'partial' },
    { feature: 'Sports Nutrition Guidance', imperial: true, others: false },
    { feature: 'Mental Conditioning', imperial: true, others: false },
    { feature: 'Progress Tracking App', imperial: true, others: false },
    { feature: 'Scholarship Programs', imperial: true, others: false },
  ];

  return (
    <>
      <Helmet>
        <title>Why Choose Us</title>
      </Helmet>
      <div className="why-choose-page">
        <div className="page-header">
          <div className="container">
            <h1>Why Choose Us</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/about">About</Link>
              <span>/</span>
              <span>Why Choose Us</span>
            </div>
          </div>
        </div>

        <section className="benefits-section section">
          <div className="container">
            <div className="section-title">
              <h2>What Sets Us Apart</h2>
              <p>
                Discover why Imperial Badminton Academy is the preferred choice for
                aspiring badminton players across the region.
              </p>
            </div>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">{benefit.icon}</div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="comparison-section section bg-light">
          <div className="container">
            <div className="section-title">
              <h2>Imperial vs Others</h2>
              <p>
                See how Imperial Badminton Academy compares to typical training centers.
              </p>
            </div>
            <div className="comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Imperial Academy</th>
                    <th>Other Academies</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((item, index) => (
                    <tr key={index}>
                      <td>{item.feature}</td>
                      <td>
                        {item.imperial === true && <FaCheck className="check" />}
                        {item.imperial === false && <FaTimes className="cross" />}
                        {item.imperial === 'partial' && <span className="partial">Limited</span>}
                      </td>
                      <td>
                        {item.others === true && <FaCheck className="check" />}
                        {item.others === false && <FaTimes className="cross" />}
                        {item.others === 'partial' && <span className="partial">Limited</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="about-cta section">
          <div className="container">
            <div className="cta-box">
              <h2>Experience the Imperial Difference</h2>
              <p>
                Don't just take our word for it. Schedule a free trial session and see
                for yourself why Imperial Badminton Academy is the right choice for you.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent">
                  Book Free Trial <FaArrowRight />
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  Explore Programs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhyChooseUs;
